import { ref, computed, nextTick, getCurrentInstance } from 'vue';
import { miniAwaitTo } from '@/utils/api';
import { useRoute } from '@/utils/vue-api';
import { useModuleData } from '@rebuild/hooks/data/useModuleData';
import { useCommonData } from '@rebuild/hooks/useCommonData';

export function useAnnouncementFullLogic({ getScrollRef }) {
	const { proxy } = getCurrentInstance();
	const { isChemExpress, isFlowTmpl } = useCommonData();
	const { announcement, announcement_tabs } = useModuleData();
	const route = useRoute();

	const activeTab = ref(0);
	const tabOption = ref([]);
	const ANNOUNCEMENT_RATE = ref([]);
	const listData = ref([
		{
			list: [],
			pagestart: 0,
			hasMore: true,
			classificationIds: '',
			emptyShow: false
		}
	]);
	const initialized = ref(false);

	const emptyData = computed(() => {
		return announcement_tabs.empty || tabOption.value.length === 0;
	});

	const isChemExpressAndFlowTmpl = computed(() => isChemExpress.value && isFlowTmpl.value);

	const currentListData = computed(() => {
		const data = listData.value[activeTab.value];
		return (
			data || {
				list: [],
				pagestart: 0,
				hasMore: true,
				classificationIds: '',
				emptyShow: false
			}
		);
	});

	async function init() {
		const [err] = await miniAwaitTo(announcement_tabs.refresh());
		if (err || !announcement_tabs.data?.data) {
			initialized.value = true;
			return;
		}

		tabOption.value = (announcement_tabs.data.data || [])
			.sort((a, b) => a.sort - b.sort)
			.filter(v => v.isPublish)
			.map(v => {
				ANNOUNCEMENT_RATE.value.push(v);
				return v.name;
			});

		if (isChemExpressAndFlowTmpl.value) {
			tabOption.value.push('ESG');
			ANNOUNCEMENT_RATE.value.push({
				id: 'ESG',
				name: 'ESG',
				isPublish: true,
				sort: 100
			});
		}

		initTab();
		loadData();
		initialized.value = true;
	}

	function initTab() {
		const { active } = route.query;
		if (active) {
			const findTabIndex = ANNOUNCEMENT_RATE.value.findIndex(v => v.id == active);
			if (findTabIndex != -1) {
				activeTab.value = findTabIndex;
			}
		}

		if (tabOption.value.length) {
			listData.value = ANNOUNCEMENT_RATE.value.map(item => {
				const currId = item.id;
				let currTabIds =
					currId != 'announcement_all'
						? [currId, ...(item.subTabList || []).map(sub => sub.id)]
								.map(v => v.slice(11).replace(/,/g, '#'))
								.join(',')
						: '';

				if (isChemExpressAndFlowTmpl.value && currId === 'ESG') {
					currTabIds = 'ESG';
				}
				return {
					list: [],
					pagestart: 0,
					hasMore: true,
					classificationIds: !currId ? '' : currTabIds,
					emptyShow: false
				};
			});
		} else {
			listData.value = [
				{
					list: [],
					pagestart: 0,
					hasMore: true,
					classificationIds: '',
					emptyShow: false
				}
			];

			if (isChemExpressAndFlowTmpl.value) {
				listData.value = [
					{
						list: [],
						pagestart: 0,
						hasMore: true,
						classificationIds: 'ESG',
						emptyShow: false
					}
				];
			}
		}
	}

	function changeActive(index) {
		activeTab.value = index;

		const scrollRef = getScrollRef?.();
		scrollRef && scrollRef.init();

		if (currentListData.value.list.length == 0 && currentListData.value.hasMore) {
			loadData(true);
		}
	}

	async function loadData(reload = false) {
		let target = listData.value[activeTab.value];

		if (!target) {
			console.warn('Target data not found for activeTab:', activeTab.value);
			return;
		}

		let params = {
			pageStart: target.pagestart,
			pageNum: 20,
			classificationIds: target.classificationIds
		};

		if (isChemExpressAndFlowTmpl.value && target.classificationIds === 'ESG') {
			params.title = 'ESG';
			delete params.classificationIds;
		}

		await announcement.refresh(params);

		const data = announcement.data?.data || [];
		const extra = announcement.data?.extra || { hasMore: false, total: 0 };

		const newData = [...target.list, ...data];
		const updatedTarget = {
			...target,
			list: reload ? data : newData,
			hasMore: extra.hasMore,
			emptyShow: newData.length == 0
		};

		proxy.$set(listData.value, activeTab.value, updatedTarget);
		await nextTick();
	}

	async function scrollPullDown(done) {
		setPageStart('pullDown');
		await loadData(true);
		done();
	}

	async function scrollPullUp(done) {
		setPageStart('pullUp');
		await loadData();
		done();
	}

	function setPageStart(type) {
		let _activeTab = activeTab.value;
		let target = listData.value[_activeTab];

		if (!target) {
			console.warn('Target data not found for activeTab:', _activeTab);
			return;
		}

		const updatedTarget = {
			...target,
			pagestart: type == 'pullDown' ? 0 : target.pagestart + 1
		};

		proxy.$set(listData.value, _activeTab, updatedTarget);
	}

	return {
		initialized,
		emptyData,
		activeTab,
		tabOption,
		currentListData,
		init,
		changeActive,
		scrollPullDown,
		scrollPullUp
	};
}
